import React, { useState } from "react";
import { Pencil, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import showcaseApi from "../../lib/showcaseApi";
import { SLOT_DEFINITIONS } from "../../lib/showcaseSlots";

const oneLine = (value) => value.replace(/\s+/g, " ").trim();
const messageFor = (error) => Array.isArray(error.data?.detail)
  ? error.data.detail.map((part) => part.msg.replace(/^Value error, /, "")).join(" ")
  : error.message || "Something went wrong. Please try again.";

export const ProductDescriptionsDialog = ({ onDescriptionChanged }) => {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rowErrors, setRowErrors] = useState({});
  const [conflicts, setConflicts] = useState({});
  const [discardOpen, setDiscardOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const hasUnsaved = products.some((p) => (drafts[p.slug] ?? p.tagline) !== p.tagline);
  const isBusy = Object.values(busy).some(Boolean);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await showcaseApi.descriptions();
      const ordered = [...result.products].sort((a, b) =>
        SLOT_DEFINITIONS.findIndex((s) => s.product === a.slug) - SLOT_DEFINITIONS.findIndex((s) => s.product === b.slug));
      setProducts(ordered);
      setDrafts(Object.fromEntries(ordered.map((p) => [p.slug, p.tagline])));
      setRowErrors({});
      setConflicts({});
    } catch (err) {
      setError(messageFor(err));
    } finally { setLoading(false); }
  };

  const changeOpen = (next) => {
    if (next) {
      setProducts([]);
      setDrafts({});
      setOpen(true);
      load();
    } else if (!isBusy) {
      if (hasUnsaved) setDiscardOpen(true);
      else setOpen(false);
    }
  };
  const apply = (product) => {
    setProducts((current) => current.map((p) => p.slug === product.slug ? product : p));
    setDrafts((current) => ({ ...current, [product.slug]: product.tagline }));
    setRowErrors((current) => ({ ...current, [product.slug]: "" }));
    setConflicts((current) => ({ ...current, [product.slug]: false }));
    onDescriptionChanged?.(product);
  };
  const save = async (product, reset = false) => {
    setBusy((current) => ({ ...current, [product.slug]: true }));
    setRowErrors((current) => ({ ...current, [product.slug]: "" }));
    try {
      const result = reset
        ? await showcaseApi.resetDescription(product.slug, product.version)
        : await showcaseApi.saveDescription(product.slug, oneLine(drafts[product.slug]), product.version);
      apply(result.product);
      toast.success(reset ? `Original description restored for ${product.display_name}` : `Saved description for ${product.display_name}`);
    } catch (err) {
      setRowErrors((current) => ({ ...current, [product.slug]: messageFor(err) }));
      if (err.status === 409) setConflicts((current) => ({ ...current, [product.slug]: true }));
    } finally {
      setBusy((current) => ({ ...current, [product.slug]: false }));
    }
  };
  const reloadRow = async (product) => {
    setBusy((current) => ({ ...current, [product.slug]: true }));
    try {
      const result = await showcaseApi.descriptions();
      const updated = result.products.find((p) => p.slug === product.slug);
      // Keep this person's draft, but update the version and show the current saved text.
      setProducts((current) => current.map((p) => p.slug === product.slug ? updated : p));
      setConflicts((current) => ({ ...current, [product.slug]: false }));
      setRowErrors((current) => ({ ...current, [product.slug]: "" }));
      onDescriptionChanged?.(updated);
    } catch (err) {
      setRowErrors((current) => ({ ...current, [product.slug]: messageFor(err) }));
    } finally { setBusy((current) => ({ ...current, [product.slug]: false })); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={changeOpen}>
        <DialogTrigger asChild>
          <Button variant="secondary" size="sm" className="min-h-11 gap-1.5" data-testid="showcase-edit-descriptions-button"><Pencil size={15} aria-hidden="true" />Edit descriptions</Button>
        </DialogTrigger>
        <DialogContent className="description-editor-dialog [&>button]:min-h-11 [&>button]:min-w-11 [&>button]:right-2 [&>button]:top-2" data-testid="showcase-edit-descriptions-dialog">
          <DialogHeader className="description-editor-header text-left">
            <DialogTitle className="font-heading text-2xl" data-testid="description-editor-title">Product descriptions</DialogTitle>
            <DialogDescription data-testid="description-editor-subtitle">Update the one-line description shown across the roadmap.</DialogDescription>
            <p className="text-xs text-muted-foreground" data-testid="showcase-edit-descriptions-shared-effect-note">Visible to everyone viewing this roadmap. Anyone with its password can edit these descriptions.</p>
          </DialogHeader>
          <div className="description-editor-body" data-testid="description-editor-body">
            {error && <Alert variant="destructive" data-testid="showcase-edit-descriptions-error-alert"><AlertDescription>{error}</AlertDescription><Button variant="outline" className="mt-3 min-h-11" onClick={load} data-testid="description-editor-retry">Try again</Button></Alert>}
            {loading ? <div className="space-y-4" data-testid="description-editor-loading">{[1, 2, 3].map((n) => <Skeleton key={n} className="h-32 w-full rounded-xl" />)}</div> : products.map((product) => {
              const value = drafts[product.slug] ?? product.tagline;
              const normalized = oneLine(value);
              const dirty = value !== product.tagline;
              const validation = normalized.length < 3 ? "Use at least three characters, or Reset to the original." : normalized.length > 160 ? "Keep the description to 160 characters." : "";
              const shownError = rowErrors[product.slug] || (dirty ? validation : "");
              const prefix = `description-${product.slug}`;
              return (
                <section key={product.slug} className="description-editor-row product-identity-section" style={{ "--product-swatch": product.swatch }} data-testid={`product-description-row-${product.slug}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label htmlFor={`${prefix}-input`} className="flex items-center gap-2 font-semibold" data-testid={`product-description-label-${product.slug}`}><span className="product-identity-dot" aria-hidden="true" />{product.display_name}</Label>
                    <span className="text-xs text-muted-foreground" data-testid={`product-description-state-${product.slug}`}>{dirty ? "Unsaved changes" : product.is_custom ? "Custom description" : "Original description"}</span>
                  </div>
                  <Input id={`${prefix}-input`} type="text" maxLength={160} value={value} disabled={busy[product.slug]} className="min-h-11"
                    onChange={(event) => { setDrafts((current) => ({ ...current, [product.slug]: event.target.value })); setRowErrors((current) => ({ ...current, [product.slug]: "" })); }}
                    aria-invalid={Boolean(shownError)} aria-describedby={`${prefix}-helper ${prefix}-count ${prefix}-error`}
                    data-testid={`product-description-input-${product.slug}`} />
                  <div className="flex items-start justify-between gap-3 text-xs text-muted-foreground">
                    <p id={`${prefix}-helper`} data-testid={`product-description-helper-${product.slug}`}>Describe what the product does, not its current progress.</p>
                    <span id={`${prefix}-count`} className="shrink-0" data-testid={`product-description-charcount-${product.slug}`}>{value.length}/160</span>
                  </div>
                  <p id={`${prefix}-error`} role={shownError ? "alert" : undefined} className="text-xs text-destructive" data-testid={`product-description-error-${product.slug}`}>{shownError}</p>
                  {conflicts[product.slug] && <Button variant="outline" className="min-h-11" onClick={() => reloadRow(product)} disabled={busy[product.slug]} data-testid={`product-description-reload-${product.slug}`}>Load latest and keep my draft</Button>}
                  {dirty && <p className="text-xs text-muted-foreground break-words" data-testid={`product-description-saved-text-${product.slug}`}>Currently saved: {product.tagline}</p>}
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" className="min-h-11" disabled={busy[product.slug] || !dirty || Boolean(validation) || conflicts[product.slug]} onClick={() => save(product)} data-testid={`product-description-save-button-${product.slug}`}>
                      {busy[product.slug] && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}{busy[product.slug] ? "Saving…" : "Save"}
                    </Button>
                    <Button size="sm" variant="ghost" className="min-h-11" disabled={busy[product.slug] || (!product.is_custom && !dirty)} onClick={() => setResetTarget(product)} data-testid={`product-description-reset-button-${product.slug}`}><RotateCcw size={14} aria-hidden="true" />Reset</Button>
                  </div>
                </section>
              );
            })}
          </div>
          <div className="description-editor-footer">
            <p className="text-xs text-muted-foreground" role="status" data-testid="showcase-edit-descriptions-unsaved-alert">{hasUnsaved ? "Some edits have not been saved." : "Use Save beside each description you change."}</p>
            <Button variant="outline" className="min-h-11" disabled={isBusy} onClick={() => changeOpen(false)} data-testid="showcase-edit-descriptions-close-button">Done</Button>
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent data-testid="showcase-edit-descriptions-discard-confirm-dialog">
          <AlertDialogHeader><AlertDialogTitle>Discard changes?</AlertDialogTitle><AlertDialogDescription>You have unsaved edits. If you close now, they’ll be lost. Descriptions you already saved will remain.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="min-h-11" data-testid="showcase-edit-descriptions-discard-cancel-button">Keep editing</AlertDialogCancel><AlertDialogAction className="min-h-11" onClick={() => { setDiscardOpen(false); setOpen(false); }} data-testid="showcase-edit-descriptions-discard-confirm-button">Discard</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={Boolean(resetTarget)} onOpenChange={(next) => { if (!next) setResetTarget(null); }}>
        <AlertDialogContent data-testid="description-reset-confirm-dialog">
          <AlertDialogHeader><AlertDialogTitle>Restore the original description?</AlertDialogTitle><AlertDialogDescription>This replaces the saved description for {resetTarget?.display_name} for everyone viewing the roadmap.</AlertDialogDescription></AlertDialogHeader>
          <p className="text-sm text-muted-foreground break-words" data-testid="description-reset-original-text">{resetTarget?.default_tagline}</p>
          <AlertDialogFooter><AlertDialogCancel className="min-h-11" data-testid="description-reset-cancel">Keep it</AlertDialogCancel><AlertDialogAction className="min-h-11" onClick={() => { const product = resetTarget; setResetTarget(null); save(product, true); }} data-testid="description-reset-confirm">Restore original</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
